import { Link, useRouterState } from "@tanstack/react-router";
import type { ComponentProps } from "react";
import { localeFromPath, localizedPathFor } from "#/lib/locale";

type LocalizedLinkProps = Omit<ComponentProps<typeof Link>, "to" | "params"> & {
	to: string;
	params?: Record<string, string>;
};

/**
 * `Link` that keeps navigation inside the current locale's URL tree: on a
 * `/ko/...` page, localizable targets (`/`, `/learn`, `/learn/$slug`) are
 * rewritten to their `/ko` mirrors so the crawlable link graph (and the user)
 * never leaks back into the English tree. Everything else — non-localizable
 * targets, unprefixed pages — passes through unchanged. The prefix comes from
 * the current URL (authoritative), not the locale atom.
 */
export function LocalizedLink({ to, params, ...props }: LocalizedLinkProps) {
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const locale = localeFromPath(pathname);
	const href = locale ? (localizedPathFor(to, locale) ?? to) : to;
	// Casts: the rewritten `to` is a runtime string; both trees (`/learn/$slug`
	// and `/ko/learn/$slug`) exist in routeTree.gen.ts, but Link's static
	// generics can't express a dynamically chosen route.
	return <Link {...props} to={href as never} params={params as never} />;
}
